import React from 'react';
import PropTypes from 'prop-types';
import { ConfirmationModal } from './ConfirmationModal';
import { i18n } from '../i18n/translate';
import { loadData, loadExternalData } from '../utils/ApiUtil';

const POLL_INTERVAL = 60000;
const TIMEOUT_WARNING_MS = 90000;
const SESSION_EXTENDING_EVENTS = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

export class AutoLogout extends React.Component {
  static propTypes = {
    clearToken: PropTypes.func,
    modalBody: PropTypes.node,
    modalTitle: PropTypes.string,
    redirectUrl: PropTypes.string,
    sessionUrl: PropTypes.string,
    authToken: PropTypes.string,
    useOauthInfo: PropTypes.bool,
    renderAsLegacy: PropTypes.bool,
  };

  static defaultProps = {
    redirectUrl: '',
    sessionUrl: '/session_status',
  };

  constructor(props) {
    super(props);

    this.state = {
      logout: false,
      warning: false,
    };
  }

  componentDidMount() {
    this.checkAndUpdateSession();
    const pollSessionStatus = setInterval(() => {
      if (!this.state.logout) {
        this.checkAndUpdateSession();
      } else {
        clearInterval(pollSessionStatus);
        window.location.reload();
      }
    }, POLL_INTERVAL);
  }

  checkAndUpdateSession = async () => {
    let isExpired;
    let secondsLeft;
    try {
      const session = await this.getSessionStatus();
      isExpired = this.props.useOauthInfo ? session.expires_in === 0 : !session.active;
      secondsLeft = this.props.useOauthInfo ? session.expires_in : session.seconds_until_expiration;
    } catch (e) {
      isExpired = true;
      secondsLeft = 0;
    }

    this.setState({
      logout: isExpired,
      warning: !isExpired && secondsLeft * 1000 <= TIMEOUT_WARNING_MS,
    });
  };

  getSessionStatus = async () =>
    this.props.useOauthInfo
      ? await loadExternalData(this.props.sessionUrl, this.props.authToken, false)
      : await loadData(this.props.sessionUrl, {}, false, true);

  addEventListeners = () => {
    SESSION_EXTENDING_EVENTS.map(event => {
      window.addEventListener(event, this.extendSession);
    });
  };

  removeEventListeners = () => {
    SESSION_EXTENDING_EVENTS.map(event => {
      window.removeEventListener(event, this.extendSession);
    });
  };

  onWarningAction = () => {
    this.addEventListeners();
    this.extendSession();
  };

  redirect = () => {
    if (this.props.redirectUrl) {
      window.location.href = encodeURI(this.props.redirectUrl);
    } else {
      window.location.reload();
    }
  };

  extendSession = () => {
    if (this.props.useOauthInfo) {
      this.props.clearToken();
      this.redirect();
      return;
    }
    this.setState({ warning: false }, () => {
      window.location.reload();
    });
  };

  get modalTitle() {
    return this.props.modalTitle || i18n.t('Your session has expired.');
  }

  get modalBody() {
    return (
      this.props.modalBody || (
        <div>{i18n.t('Unfortunately, your session has expired and we require you to login again.')}</div>
      )
    );
  }

  render() {
    return (
      <div>
        {this.state.logout && (
          <ConfirmationModal
            title={this.modalTitle}
            onConfirm={this.redirect}
            canClose={false}
            show={true}
            confirmOnly={true}
          >
            {this.modalBody}
          </ConfirmationModal>
        )}
        {this.state.warning && (
          <ConfirmationModal
            renderAsLegacy={this.props.renderAsLegacy}
            title={i18n.t('Your session is about to expire.')}
            onConfirm={this.onWarningAction}
            canClose={false}
            show={true}
            confirmOnly={true}
            confirmButtonText={i18n.t('Yes')}
          >
            {i18n.t('Would you like to stay logged in?')}
          </ConfirmationModal>
        )}
      </div>
    );
  }
}
